import { DateUtil } from '@/utils'
import { childGameToSubMatch, resolveParentDetailSubMatches } from './child-game-sync'
import { parseTeamClothesColorMap, resolveApiClothesColor } from '@/views/match-manage/wheel-battle/utils/jersey-colors'
import { resolveUnionMatchIcon } from '@/views/match-manage/wheel-battle/utils/team-logo'

function legacyStatusFromState(state) {
  if (state === 1) {
    return { status: 'inProgress', statusLabel: '进行中' }
  }
  if (state === 2) {
    return { status: 'ended', statusLabel: '已结束' }
  }
  return { status: 'notStarted', statusLabel: '未开始' }
}

function parseLegacyBeginDate(record) {
  var beginDate = record.parentGame && record.parentGame.beginDate
  if (!beginDate) {
    return null
  }
  return DateUtil.getDateObject(beginDate)
}

function deriveGroupName(parentGame) {
  if (!parentGame || !parentGame.name) {
    return ''
  }
  var name = String(parentGame.name)
  if (name.indexOf('车轮战') !== -1) {
    return name.replace(/车轮战$/, '')
  }
  return name
}

/**
 * 详情 stageRule → 基本信息页规则展示
 */
export function stageRuleToDisplayRules(stageRule) {
  var rule = stageRule || {}
  return {
    rotationType: rule.rotationType || rule.wheelRuleType || '',
    duration: rule.duration,
    durationMinutes: rule.duration,
    singleGameDuration: rule.singleGameDuration,
    winCondition: rule.winCondition || '',
    offRuleText: rule.offRuleText || rule.doubleOffRule || '',
    tieEnableText: rule.tieEnableText || ''
  }
}

function resolveTeamSource(team) {
  if (!team) {
    return null
  }
  if (team.team && typeof team.team === 'object') {
    return Object.assign({}, team.team, {
      id: team.team.id != null ? team.team.id : team.id,
      teamId: team.team.teamId != null ? team.team.teamId : team.teamId,
      clothesColor: team.clothesColor || team.team.clothesColor
    })
  }
  return team
}

function getTeamId(team) {
  if (!team) {
    return ''
  }
  if (team.id != null && team.id !== '') {
    return String(team.id)
  }
  if (team.teamId != null && team.teamId !== '') {
    return String(team.teamId)
  }
  return ''
}

function readTeamClothesColor(team) {
  if (!team) {
    return ''
  }
  return resolveApiClothesColor(
    team.clothesColor ||
    team.clothesColors ||
    team.jerseyColor ||
    team.homeClothesColor ||
    team.awayClothesColor ||
    team.color
  )
}

function collectTeamOrderSources(apiDetail) {
  if (!apiDetail) {
    return []
  }
  var parentGame = apiDetail.parentGame || {}
  return [
    apiDetail.initialOrder,
    parentGame.initialOrder,
    apiDetail.rotationOrder,
    parentGame.rotationOrder,
    apiDetail.waitingTeams,
    parentGame.waitingTeams
  ]
}

function buildTeamClothesColorMap(apiDetail) {
  var map = {}
  if (!apiDetail) {
    return map
  }

  var parentGame = apiDetail.parentGame || {}
  var sources = [
    apiDetail.teamClothesColors,
    apiDetail.teamJerseyColors,
    parentGame.teamClothesColors,
    parentGame.teamJerseyColors
  ]

  sources.forEach(function (source) {
    var parsed = parseTeamClothesColorMap(source)
    Object.keys(parsed).forEach(function (key) {
      map[key] = parsed[key]
    })
  })

  collectTeamOrderSources(apiDetail).forEach(function (order) {
    var teamList = order || []
    teamList.forEach(function (rawTeam) {
      var team = resolveTeamSource(rawTeam)
      var id = getTeamId(team)
      if (!id) {
        return
      }
      var color = readTeamClothesColor(team)
      if (color) {
        map[id] = color
      }
    })
  })

  return map
}

function normalizeTeamItem(team, colorMap) {
  team = resolveTeamSource(team)
  if (!team) {
    return null
  }
  var id = getTeamId(team)
  if (!id) {
    return null
  }
  var clothesColor = readTeamClothesColor(team)
  if (!clothesColor && colorMap && colorMap[id]) {
    clothesColor = colorMap[id]
  }
  return {
    id: id,
    name: team.name || team.teamName || '',
    badge: team.badge || team.logo || '',
    clothesColor: clothesColor
  }
}

function normalizeTeamList(order, colorMap) {
  return (order || []).map(function (team) {
    return normalizeTeamItem(team, colorMap)
  }).filter(function (item) {
    return !!item
  })
}

/**
 * 出赛球队展示顺序：优先 initialOrder，兼容旧接口仅返回 rotationOrder
 */
export function resolveInitialOrder(apiDetail) {
  var colorMap = buildTeamClothesColorMap(apiDetail)
  var parentGame = apiDetail && apiDetail.parentGame
  var order = apiDetail && apiDetail.initialOrder
  if ((!order || !order.length) && parentGame && parentGame.initialOrder) {
    order = parentGame.initialOrder
  }
  if (!order || !order.length) {
    order = apiDetail && apiDetail.rotationOrder
  }
  if ((!order || !order.length) && parentGame && parentGame.rotationOrder) {
    order = parentGame.rotationOrder
  }
  return normalizeTeamList(order, colorMap)
}

/**
 * 当前轮转队列：用于候场球队、下一场默认主客队
 */
export function resolveRotationOrder(apiDetail) {
  var colorMap = buildTeamClothesColorMap(apiDetail)
  var order = apiDetail && apiDetail.rotationOrder
  if ((!order || !order.length) && apiDetail && apiDetail.parentGame) {
    order = apiDetail.parentGame.rotationOrder
  }
  return normalizeTeamList(order, colorMap)
}

/**
 * standing 列表 → 页面展示结构
 */
export function normalizeStandingList(standing) {
  return (standing || []).map(function (item) {
    var team = item.team || {}
    return {
      rank: item.rank != null ? item.rank : 0,
      team: {
        id: team.id != null ? String(team.id) : (item.id != null ? String(item.id) : ''),
        name: team.name || item.name || '',
        badge: team.badge || item.badge || ''
      },
      played: item.played || 0,
      win: item.win || 0,
      tie: item.tie || 0,
      lose: item.lose || 0,
      points: item.points || 0
    }
  })
}

function normalizeChildGameTeamFromDetail(childGame, side) {
  var nested = side === 'home' ? childGame.homeTeam : childGame.awayTeam
  var flatId = side === 'home' ? childGame.homeTeamId : childGame.awayTeamId
  var flatName = side === 'home' ? childGame.homeTeamName : childGame.awayTeamName
  if (nested && (nested.id != null || nested.name)) {
    return normalizeTeamItem(nested)
  }
  if (flatId != null || flatName) {
    return normalizeTeamItem({
      id: flatId,
      name: flatName,
      badge: ''
    })
  }
  return null
}

/**
 * API 详情 → 页面 detail 结构
 */
export function legacyDetailFromRecord(record, gameId) {
  if (!record || !record.parentGame) {
    return null
  }
  var pg = record.parentGame
  var stateInfo = legacyStatusFromState(pg.state)
  var groupName = deriveGroupName(pg)

  return {
    id: gameId || pg.id,
    sequenceNo: pg.sequenceNo != null ? String(pg.sequenceNo) : '01',
    status: stateInfo.status,
    statusLabel: pg.stateName || stateInfo.statusLabel,
    groupId: pg.groupId,
    groupName: groupName,
    form: {
      roundId: pg.stageId != null ? String(pg.stageId) : '',
      roundLabel: pg.stageName || '',
      beginDate: parseLegacyBeginDate(record),
      stadiumName: pg.location || '',
      pitchId: pg.fieldId ? String(pg.fieldId) : '',
      pitchName: pg.fieldName || '',
      groupId: pg.groupId,
      groupName: groupName
    },
    rules: stageRuleToDisplayRules(record.stageRule)
  }
}

/**
 * AdminGet 响应 → 页面状态
 */
export function pageStateFromApiDetail(apiDetail) {
  if (!apiDetail || !apiDetail.parentGame) {
    return null
  }
  var gameId = apiDetail.parentGame.id
  var legacy = legacyDetailFromRecord(apiDetail, gameId)
  if (!legacy) {
    return null
  }
  legacy.parentGameName = apiDetail.parentGame.name || ''
  var subMatchState = resolveParentDetailSubMatches(apiDetail)
  return {
    legacy: legacy,
    form: legacy.form,
    rules: legacy.rules,
    participatingTeams: resolveInitialOrder(apiDetail),
    rotationTeams: resolveRotationOrder(apiDetail),
    subMatches: subMatchState.subMatches,
    currentSubMatch: subMatchState.currentSubMatch,
    standing: normalizeStandingList(apiDetail.standing),
    unionMatchIcon: resolveUnionMatchIcon(apiDetail.parentGame),
    apiDetail: apiDetail
  }
}

/**
 * 确认比分后详情 → 成功弹层展示数据
 */
export function buildScoreSubmitResultFromChildGameDetail(apiDetail) {
  if (!apiDetail || !apiDetail.childGame) {
    return null
  }
  var childGame = apiDetail.childGame
  var homeTeam = normalizeChildGameTeamFromDetail(childGame, 'home')
  var awayTeam = normalizeChildGameTeamFromDetail(childGame, 'away')
  if (!homeTeam || !awayTeam) {
    return null
  }
  var orderAfter = (childGame.rotationOrderAfter || [])
    .map(function (team) {
      return normalizeTeamItem(team)
    })
    .filter(function (item) {
      return !!item
    })
  var nextMatch = null
  if (orderAfter.length >= 2) {
    nextMatch = {
      defender: orderAfter[0],
      challenger: orderAfter[1]
    }
  }
  if (childGame.resultType === 2) {
    return {
      winnerLabel: '双下',
      rotationLabel: homeTeam.name + '、' + awayTeam.name + '进入车轮末位',
      winnerTeam: null,
      loserTeam: null,
      nextMatch: nextMatch
    }
  }
  var winnerTeam = homeTeam
  var loserTeam = awayTeam
  var winnerId = childGame.winnerTeamId != null ? String(childGame.winnerTeamId) : ''
  if (winnerId && winnerId === awayTeam.id) {
    winnerTeam = awayTeam
    loserTeam = homeTeam
  } else if (childGame.homeScore < childGame.awayScore) {
    winnerTeam = awayTeam
    loserTeam = homeTeam
  }
  return {
    winnerLabel: winnerTeam.name + '胜出',
    rotationLabel: loserTeam.name + '进入车轮末位',
    winnerTeam: winnerTeam,
    loserTeam: loserTeam,
    nextMatch: nextMatch
  }
}

/**
 * 子场次详情 → 录入比分页状态
 */
export function scoreEntryStateFromChildGameDetail(apiDetail) {
  if (!apiDetail || !apiDetail.childGame) {
    return null
  }
  var childGame = apiDetail.childGame
  var parentGame = apiDetail.parentGame || {}
  var subMatch = childGameToSubMatch(childGame, { isCurrent: childGame.resultType === 0 })
  if (!subMatch) {
    return null
  }
  return {
    subMatch: subMatch,
    parentGameId: parentGame.id != null
      ? String(parentGame.id)
      : (childGame.parentGameId != null ? String(childGame.parentGameId) : ''),
    childGameId: childGame.id != null ? String(childGame.id) : '',
    homeScore: childGame.homeScore,
    awayScore: childGame.awayScore,
    resultType: childGame.resultType,
    isConfirmed: childGame.resultType !== 0,
    rotationOrderBefore: (childGame.rotationOrderBefore || []).map(function (team) {
      return normalizeTeamItem(team)
    }).filter(Boolean),
    rotationOrderAfter: (childGame.rotationOrderAfter || []).map(function (team) {
      return normalizeTeamItem(team)
    }).filter(Boolean),
    parentGame: parentGame,
    unionMatchIcon: resolveUnionMatchIcon(parentGame),
    apiDetail: apiDetail
  }
}

/**
 * 阶段分组接口中，未分配小组的球队（groupId 为空）
 */
export function isUnassignedStageTeam(team) {
  if (!team) {
    return false
  }
  if (team.groupId == null || team.groupId === undefined) {
    return true
  }
  return String(team.groupId).trim() === ''
}

/**
 * AdminGetBMMatchStageGroupList.data → 候选球队源列表
 */
export function resolveUnassignedTeamsFromStageGroup(stageData) {
  return (stageData && stageData.teams ? stageData.teams : []).filter(isUnassignedStageTeam)
}

/**
 * 从阶段分组列表解析指定小组容量
 */
export function resolveStageGroupTeamTotal(stageData, groupId) {
  if (!stageData || !groupId) {
    return 0
  }
  var list = stageData.list || []
  for (var i = 0; i < list.length; i++) {
    if (String(list[i].id) === String(groupId)) {
      var total = list[i].groupTeamTotal != null
        ? list[i].groupTeamTotal
        : list[i].teamTotal
      return total != null ? Number(total) || 0 : 0
    }
  }
  return 0
}

/**
 * 编辑出赛球队页：详情 + 小组球队 → 出战/候选列表
 */
export function buildTeamEditStateFromApiDetail(apiDetail, groupTeams, stageData) {
  if (!apiDetail || !apiDetail.parentGame) {
    return null
  }
  var pg = apiDetail.parentGame
  var groupId = pg.groupId ? String(pg.groupId) : ''
  var participatingTeams = resolveInitialOrder(apiDetail)
  var participatingIdMap = {}
  participatingTeams.forEach(function (team) {
    participatingIdMap[team.id] = true
  })
  var candidateTeams = (groupTeams || []).map(function (team) {
    return normalizeTeamItem(team)
  }).filter(function (team) {
    return team && !participatingIdMap[team.id]
  })
  return {
    participatingTeams: participatingTeams,
    candidateTeams: candidateTeams,
    groupName: deriveGroupName(pg),
    matchId: pg.matchId ? String(pg.matchId) : '',
    stageId: pg.stageId ? String(pg.stageId) : '',
    groupId: groupId,
    groupTeamTotal: resolveStageGroupTeamTotal(stageData, groupId),
    unionMatchIcon: resolveUnionMatchIcon(pg)
  }
}
